import { useCards } from "@/pages/Dashboard";
import { AlertTriangle, Clock, ChevronDown } from "lucide-react";
import { useState, useMemo } from "react";

const statusColors = {
  "In Progress": "border-l-blue-500",
  Completed: "border-l-emerald-500",
  Blocked: "border-l-red-500",
};

const stageBadge = {
  POC: "bg-blue-100 text-blue-700",
  Pilot: "bg-amber-100 text-amber-700",
  Production: "bg-emerald-100 text-emerald-700",
  Support: "bg-purple-100 text-purple-700",
};

// Helper to parse JSON strings safely
function parseJSON(str) {
  if (!str) return null;
  try {
    return typeof str === "string" ? JSON.parse(str) : str;
  } catch {
    return null;
  }
}

function getBaseComponent(layerKey) {
  const colonIdx = layerKey.indexOf(": ");
  const group = colonIdx > -1 ? layerKey.substring(0, colonIdx) : layerKey;
  const bracketIdx = group.indexOf(" [");
  return bracketIdx > -1 ? group.substring(0, bracketIdx) : group;
}

function ComponentProgress({ card, component, onClick }) {
  const layerStatuses =
    parseJSON(card.layerStatuses) || card.layer_statuses || {};
  const layers = Object.entries(layerStatuses).filter(
    ([k]) => getBaseComponent(k) === component,
  );
  if (layers.length === 0) return null;

  const done = layers.filter(([, v]) => v.status === "Done").length;
  const blocked = layers.filter(([, v]) => v.status === "Blocked").length;
  const testing = layers.filter(([, v]) => v.status === "Testing").length;
  const total = layers.length;
  const percent = Math.round((done / total) * 100);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="flex items-center gap-2 text-xs hover:bg-slate-100 px-1.5 py-0.5 rounded transition-colors"
      data-testid={`comp-progress-${component.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <span className="text-slate-600 w-16 text-left truncate">
        {component.split(" ")[0]}
      </span>
      <div className="w-12 h-1 bg-slate-200 rounded overflow-hidden">
        <div
          className={`h-full ${blocked > 0 ? "bg-red-500" : testing > 0 ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${percent}%` }}
        ></div>
      </div>
      {blocked > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />}
    </button>
  );
}

function CardItem({ card }) {
  const { openCardWithComponent } = useCards();

  // Map API response to component expectations
  const layerStatuses =
    parseJSON(card.layerStatuses) || card.layer_statuses || {};
  const hardwareConfigs =
    parseJSON(card.hardwareConfigs) || card.hardware_configs || {};
  const tags = parseJSON(card.tags) || [];
  const attachments = parseJSON(card.attachments) || card.files || [];

  const total = Object.keys(layerStatuses).length;
  const done = Object.values(layerStatuses).filter(
    (l) => l.status === "Done",
  ).length;
  const progress =
    card.progressPercentage ||
    (total > 0 ? Math.round((done / total) * 100) : 0);
  const hwConfigs = Array.isArray(hardwareConfigs)
    ? hardwareConfigs
    : Object.values(hardwareConfigs || {});

  // Get unique base components
  const baseComponents = useMemo(() => {
    const bases = new Set();
    Object.keys(layerStatuses).forEach((k) => bases.add(getBaseComponent(k)));
    return Array.from(bases);
  }, [layerStatuses]);

  // Compute timeline from layers
  const timeline = useMemo(() => {
    let earliest = null,
      latest = null;
    Object.values(layerStatuses).forEach((l) => {
      if (l.start_date && (!earliest || l.start_date < earliest))
        earliest = l.start_date;
      if (l.end_date && (!latest || l.end_date > latest)) latest = l.end_date;
    });
    return { earliest, latest };
  }, [layerStatuses]);

  // Map status to color
  const statusColor = statusColors[card.status] || "border-l-slate-500";

  return (
    <div
      onClick={() => openCardWithComponent(card)}
      className={`bg-white border border-l-4 rounded p-3 cursor-pointer hover:shadow-sm transition-shadow ${statusColor}`}
      data-testid={`card-${card.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {card.id && (
            <span className="font-mono text-xs font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
              {card.id.substring(0, 8).toUpperCase()}
            </span>
          )}
          {card.priority && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                card.priority === "High"
                  ? "bg-red-100 text-red-700"
                  : card.priority === "Medium"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-blue-100 text-blue-700"
              }`}
            >
              {card.priority}
            </span>
          )}
          {hwConfigs.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
              Multi-HW
            </span>
          )}
        </div>
        {attachments.length > 0 && (
          <span className="text-xs text-slate-500 px-1.5 py-0.5">
            📎 {attachments.length}
          </span>
        )}
      </div>

      <p className="text-sm font-medium text-slate-900 mb-1">{card.title}</p>
      <p className="text-xs text-slate-500 mb-2">
        {card.assignee} | {card.status}
      </p>
      {card.description && (
        <p className="text-xs text-slate-400 mb-2 line-clamp-1">
          {card.description}
        </p>
      )}

      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <span className="text-xs text-slate-500">
          {done}/{total}
        </span>
      </div>

      <div className="flex flex-wrap gap-1 border-t border-slate-100 pt-2 mt-2">
        {baseComponents.slice(0, 5).map((comp) => (
          <ComponentProgress
            key={comp}
            card={card}
            component={comp}
            onClick={() => openCardWithComponent(card, comp)}
          />
        ))}
        {baseComponents.length > 5 && (
          <span className="text-xs text-slate-400">
            +{baseComponents.length - 5} more
          </span>
        )}
      </div>

      {timeline.latest && (
        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {timeline.earliest || "?"} -{" "}
          {timeline.latest}
        </p>
      )}

      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded"
            >
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="text-[10px] text-slate-500">
              +{tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ArchiveSection({ cards, title }) {
  const [expanded, setExpanded] = useState(false);
  if (cards.length === 0) return null;

  return (
    <div className="mt-6 pt-4 border-t border-slate-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-3"
        data-testid="archive-toggle"
      >
        <ChevronDown
          className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
        {title} ({cards.length})
      </button>
      {expanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 opacity-60">
          {cards.map((card) => (
            <CardItem key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function StatusOverview({ activeCards, archivedCards }) {
  if (activeCards.length === 0 && archivedCards.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">No cards found</div>
    );
  }

  return (
    <div data-testid="status-overview">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeCards.map((card) => (
          <CardItem key={card.id} card={card} />
        ))}
      </div>
      {activeCards.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          No active cards match your filters
        </div>
      )}
      <ArchiveSection cards={archivedCards} title="Completed / Archived" />
    </div>
  );
}
